import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import {
  assignSuperAdminClientSalon,
  fetchAllBusinesses,
  fetchAllSalons,
  fetchSuperAdminClients,
} from "../api/superadmin";
import { useAuth } from "../context/AuthContext";
import { BTN_OUTLINE, BTN_PRIMARY, FIELD_CLASS, LABEL_TEXT, MODAL_TEXT } from "../ui/modalStyles";

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page does",
    description: "Move a client record from one salon to another salon.",
    anchorId: "ca-overview",
    bullets: [
      "Search clients by name, email, or phone.",
      "Filter by current salon when needed.",
      "Choose a target salon and save assignment.",
    ],
    tips: [
      "Confirm the destination salon before saving.",
      "Run a new search after updates to verify changes.",
    ],
  },
  {
    id: "filters",
    title: "Filters",
    description: "Use these controls to find the correct client records quickly.",
    anchorId: "ca-filters",
    bullets: [
      "Search term supports name, email, and phone.",
      "Business filter narrows salon choices.",
      "Current salon limits the results list to one location.",
    ],
    tips: [
      "Use at least 2 search characters for better results.",
    ],
  },
  {
    id: "results",
    title: "Assignment results",
    description: "Review each client, pick the destination salon, and assign.",
    anchorId: "ca-results",
    bullets: [
      "Current salon is shown for each client row.",
      "Assign button is enabled only when the destination differs.",
      "Successful assignment updates the row immediately.",
    ],
    tips: [
      "If assignment fails, verify your super admin access and retry.",
    ],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Client not found",
    body: "Use a longer search term or narrow with Current Salon before running search.",
  },
  {
    title: "Assignment button disabled",
    body: "Select a destination salon different from the current salon.",
  },
  {
    title: "Unable to save assignment",
    body: "Refresh the page and confirm the client and salon still exist.",
  },
];

const TOOLTIPS = {
  search: "Search by client name, email, or phone.",
  business: "Optional. Narrows the salon lists to one business.",
  current_salon: "Optional. Shows only clients currently assigned to this salon.",
  destination_salon: "The salon this client will be moved to.",
  assign: "Saves the selected destination salon for this client.",
};

const getClientId = (row) => Number(row?.id ?? row?.client_id ?? 0);

const getClientName = (row) => {
  const fullName = String(row?.full_name || "").trim();
  if (fullName) return fullName;
  const first = String(row?.first_name || "").trim();
  const last = String(row?.last_name || "").trim();
  const fallback = `${first} ${last}`.trim();
  if (fallback) return fallback;
  const id = getClientId(row);
  return id > 0 ? `Client #${id}` : "Unknown client";
};

export default function SuperAdminClientAssignment() {
  const { is_super_admin } = useAuth();

  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [currentSalonId, setCurrentSalonId] = useState("");

  const [clients, setClients] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const [draftSalonByClient, setDraftSalonByClient] = useState({});
  const [savingClientId, setSavingClientId] = useState(null);

  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const salonNameById = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((salon) => {
      const id = Number(salon?.id ?? salon?.salon_id);
      if (!Number.isFinite(id)) return;
      map.set(id, salon?.name || `Salon ${id}`);
    });
    return map;
  }, [salons]);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (salon) => String(salon?.business_id ?? "") === String(businessId)
    );
  }, [salons, businessId]);

  const assignmentSalons = useMemo(
    () => (filteredSalons.length ? filteredSalons : salons),
    [filteredSalons, salons]
  );

  useEffect(() => {
    if (!businessId || !currentSalonId) return;
    const hasSalon = filteredSalons.some(
      (salon) => String(salon?.id ?? "") === String(currentSalonId)
    );
    if (!hasSalon) {
      setCurrentSalonId("");
    }
  }, [businessId, currentSalonId, filteredSalons]);

  const loadMetadata = useCallback(async () => {
    setMetadataLoading(true);
    setMetadataError("");
    try {
      const [businessData, salonData] = await Promise.all([
        fetchAllBusinesses(),
        fetchAllSalons(),
      ]);
      setBusinesses(Array.isArray(businessData) ? businessData : []);
      setSalons(Array.isArray(salonData) ? salonData : []);
    } catch (error) {
      setMetadataError(error?.message || "Unable to load businesses and salons.");
      setBusinesses([]);
      setSalons([]);
    } finally {
      setMetadataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!is_super_admin) return;
    loadMetadata();
  }, [is_super_admin, loadMetadata]);

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const runSearch = useCallback(async () => {
    const trimmed = String(searchTerm || "").trim();
    if (!trimmed && !currentSalonId) {
      setSearchError("Enter a search term or choose Current Salon.");
      setClients([]);
      return;
    }
    if (trimmed && trimmed.length < 2) {
      setSearchError("Enter at least 2 characters for search.");
      setClients([]);
      return;
    }

    setSearchLoading(true);
    setSearchError("");
    setStatusMessage("");
    try {
      const params = {};
      if (trimmed) {
        params.search = trimmed;
      }
      if (businessId) {
        params.business_id = Number(businessId);
      }
      if (currentSalonId) {
        params.salon_id = Number(currentSalonId);
      }
      params.limit = 200;
      params.require_query = !currentSalonId && !businessId;
      const data = await fetchSuperAdminClients(params);
      const rows = Array.isArray(data) ? data : [];
      setClients(rows);
      setDraftSalonByClient((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          const id = getClientId(row);
          if (!id) return;
          if (next[id] !== undefined && next[id] !== null && next[id] !== "") return;
          const salonIdValue = Number(row?.salon_id);
          next[id] = Number.isFinite(salonIdValue) ? salonIdValue : "";
        });
        return next;
      });
    } catch (error) {
      setClients([]);
      setSearchError(error?.message || "Unable to search clients.");
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm, businessId, currentSalonId]);

  const clearFilters = () => {
    setSearchTerm("");
    setBusinessId("");
    setCurrentSalonId("");
    setClients([]);
    setDraftSalonByClient({});
    setSearchError("");
    setStatusMessage("");
  };

  const assignClientSalon = async (client) => {
    const clientId = getClientId(client);
    if (!clientId) return;
    const destinationId = Number(draftSalonByClient[clientId]);
    const currentId = Number(client?.salon_id || 0);

    if (!Number.isFinite(destinationId) || destinationId <= 0) {
      setSearchError("Choose a destination salon.");
      return;
    }
    if (destinationId === currentId) {
      setSearchError("Destination salon must be different from current salon.");
      return;
    }

    setSavingClientId(clientId);
    setSearchError("");
    setStatusMessage("");
    try {
      await assignSuperAdminClientSalon(clientId, destinationId);
      setClients((prev) =>
        prev.map((row) =>
          getClientId(row) === clientId
            ? {
                ...row,
                salon_id: destinationId,
                salon_name: salonNameById.get(destinationId) || row?.salon_name || "",
              }
            : row
        )
      );
      setStatusMessage(
        `${getClientName(client)} was moved to ${salonNameById.get(destinationId) || `Salon ${destinationId}`}.`
      );
    } catch (error) {
      setSearchError(error?.message || "Unable to assign client to salon.");
    } finally {
      setSavingClientId(null);
    }
  };

  if (!is_super_admin) {
    return (
      <div className="px-4 py-6">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Client Assignment" closeTo="/superadmin" />

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Client Assignment"
              subtitle="Move client records to the correct salon from one page."
              sections={HELP_SECTIONS}
              troubleshooting={TROUBLESHOOTING_ITEMS}
              onJumpToSection={jumpToSectionWithHighlight}
            />

            {metadataError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {metadataError}
              </div>
            ) : null}

            {searchError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {searchError}
              </div>
            ) : null}

            {statusMessage ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {statusMessage}
              </div>
            ) : null}

            <div
              id="ca-overview"
              className={`rounded-2xl border border-slate-200 bg-white p-5 ${
                highlightedSectionId === "ca-overview"
                  ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
                  : ""
              }`}
            >
              <h2 className="text-base md:text-lg font-semibold text-slate-900">
                Assignment Overview
              </h2>
              <p className="mt-2 text-sm md:text-base text-slate-600">
                Search for clients, review their current salon, then assign them to a new salon.
                This page is only available to Super Admin users.
              </p>
            </div>

            <div
              id="ca-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${
                highlightedSectionId === "ca-filters"
                  ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
                  : ""
              }`}
            >
              <h2 className="text-base md:text-lg font-semibold text-slate-900">Filters</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="xl:col-span-2">
                  <div className="flex items-center gap-2">
                    <label className={LABEL_TEXT}>Search Client</label>
                    <InfoTooltip text={TOOLTIPS.search} label="Search Client" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="Name, email, or phone"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <label className={LABEL_TEXT}>Business</label>
                    <InfoTooltip text={TOOLTIPS.business} label="Business" />
                  </div>
                  <select
                    value={businessId}
                    onChange={(event) => setBusinessId(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={metadataLoading}
                  >
                    <option value="">All Businesses</option>
                    {businesses.map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code ? `${business.code} - ` : ""}
                        {business.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <label className={LABEL_TEXT}>Current Salon</label>
                    <InfoTooltip text={TOOLTIPS.current_salon} label="Current Salon" />
                  </div>
                  <select
                    value={currentSalonId}
                    onChange={(event) => setCurrentSalonId(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={metadataLoading}
                  >
                    <option value="">All Salons</option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={runSearch}
                  className={BTN_PRIMARY}
                  disabled={searchLoading || metadataLoading}
                >
                  {searchLoading ? "Searching..." : "Run Search"}
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className={BTN_OUTLINE}
                  disabled={searchLoading}
                >
                  Clear
                </button>
              </div>
            </div>

            <div
              id="ca-results"
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${
                highlightedSectionId === "ca-results"
                  ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
                  : ""
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-base md:text-lg font-semibold text-slate-900">Results</h2>
                <span className="text-sm text-slate-500">
                  {clients.length} client{clients.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px] text-sm md:text-base">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Client</th>
                      <th className="px-4 py-3 text-left font-semibold">Email</th>
                      <th className="px-4 py-3 text-left font-semibold">Phone</th>
                      <th className="px-4 py-3 text-left font-semibold">Current Salon</th>
                      <th className="px-4 py-3 text-left font-semibold">
                        <span className="inline-flex items-center gap-2">
                          Assign To
                          <InfoTooltip
                            text={TOOLTIPS.destination_salon}
                            label="Assign To"
                          />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        <span className="inline-flex items-center gap-2">
                          Action
                          <InfoTooltip text={TOOLTIPS.assign} label="Action" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                          {searchLoading
                            ? "Searching clients..."
                            : "No clients loaded yet. Run a search to start."}
                        </td>
                      </tr>
                    ) : (
                      clients.map((client) => {
                        const clientId = getClientId(client);
                        const currentId = Number(client?.salon_id || 0);
                        const selectedId = Number(draftSalonByClient[clientId] || 0);
                        const isSaving = savingClientId === clientId;
                        const disableAssign =
                          !selectedId || !clientId || selectedId === currentId || isSaving;
                        return (
                          <tr key={clientId} className="border-t border-slate-100">
                            <td className="px-4 py-3 text-slate-800">{getClientName(client)}</td>
                            <td className="px-4 py-3 text-slate-700">{client?.email || "-"}</td>
                            <td className="px-4 py-3 text-slate-700">{client?.phone || "-"}</td>
                            <td className="px-4 py-3 text-slate-700">
                              {salonNameById.get(currentId) || "-"}
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={draftSalonByClient[clientId] ?? (currentId || "")}
                                onChange={(event) =>
                                  setDraftSalonByClient((prev) => ({
                                    ...prev,
                                    [clientId]: Number(event.target.value) || "",
                                  }))
                                }
                                className={`${FIELD_CLASS} w-full`}
                              >
                                <option value="">Select salon</option>
                                {assignmentSalons.map((salon) => (
                                  <option key={salon.id} value={salon.id}>
                                    {salon.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => assignClientSalon(client)}
                                className={BTN_PRIMARY}
                                disabled={disableAssign}
                              >
                                {isSaving ? "Assigning..." : "Assign"}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
